import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, Subscription, interval } from 'rxjs';
import { ApiService, RequestDataResponse, RequestListItem } from '../../../shared/services/api.service';
import { MatDialog } from '@angular/material/dialog';
import { ConsentData, ConsentModalComponent } from '../../../shared/components/consent-modal/consent-modal.component';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { exportRecordsPdf, RecordPdfRow } from '../../../shared/utils/pdf-export';
import { exportRecordsExcel } from '../../../shared/utils/excel-export';

@Component({
  selector: 'app-multi-search',
  templateUrl: './multi-search.component.html',
  styleUrls: ['./multi-search.component.css']
})
export class MultiSearchComponent implements OnInit, OnDestroy {
  displayedColumns: string[] = ['customerName', 'searchCriteria', 'status', 'createdAt', 'availability', 'actions'];
  dataSource = new MatTableDataSource<RequestListItem>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  requestBatchId = '';
  isLoading = false;
  isDownloading = false;
  batchLabel = 'Multi Search';
  progressLabel = '';
  batchSource: 'NEW_REQUEST' | 'ADDRESS_BOOK' | 'CSV_UPLOAD' = 'NEW_REQUEST';
  private requestsInInputOrder: RequestListItem[] = [];
  private openedFromAddressBook = false;
  private openedFromUserAddressBook = false;
  private pollSub: Subscription | null = null;
  private lastCompletionChargeTotal: number | null = null;
  private pendingNavigationConfirmation: Promise<boolean> | null = null;
  private dncLoadingByRequestId = new Set<string>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private analyticsService: AnalyticsService
  ) {}

  ngOnInit(): void {
    this.requestBatchId = this.route.snapshot.paramMap.get('requestBatchId') || '';
    const from = this.route.snapshot.queryParamMap.get('from');
    this.openedFromAddressBook = from === 'address-book';
    this.openedFromUserAddressBook = from === 'user-address-book';
    if (!this.requestBatchId) {
      void this.router.navigate(['/admin/history']);
      return;
    }
    this.loadRequests(true);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!this.hasActiveSearchItems()) {
      return;
    }
    event.preventDefault();
    // Browser controls the exact text shown.
    event.returnValue = 'Your search is still in progress.';
  }

  get hasRequests(): boolean {
    return this.dataSource.data.length > 0;
  }

  get hasActiveRequests(): boolean {
    return this.hasActiveSearchItems();
  }

  get downloadDisabledTooltip(): string {
    if (this.hasActiveRequests) {
      return 'Search in progress. Please wait.';
    }
    return 'Download as PDF or Excel';
  }

  canDeactivate(): boolean | Promise<boolean> {
    if (!this.hasActiveSearchItems()) {
      return true;
    }
    if (this.pendingNavigationConfirmation) {
      return this.pendingNavigationConfirmation;
    }

    const dialogRef = this.dialog.open(ConsentModalComponent, {
      width: '620px',
      disableClose: true,
      autoFocus: false,
      data: {
        title: 'Search still in progress',
        body: [
          'Your search is still in progress.',
          'Are you sure you want to navigate away? The search will continue in the background.'
        ],
        acceptLabel: 'Leave page',
        rejectLabel: 'Stay here',
      },
    });

    this.pendingNavigationConfirmation = firstValueFrom(dialogRef.afterClosed())
      .then((result) => result?.action === 'accept')
      .catch(() => false)
      .finally(() => {
        this.pendingNavigationConfirmation = null;
      });

    return this.pendingNavigationConfirmation;
  }

  get backButtonLabel(): string {
    if (this.openedFromAddressBook || this.openedFromUserAddressBook) {
      return 'Back to address book';
    }
    if (this.batchSource === 'CSV_UPLOAD') {
      return 'Back to search';
    }
    return 'Back to address book';
  }

  loadRequests(showLoader: boolean): void {
    if (showLoader) this.isLoading = true;
    this.apiService.listMultiRequestItems(this.requestBatchId).subscribe({
      next: (response) => {
        this.dataSource.data = response.requests;
        this.requestsInInputOrder = [...response.requests];
        this.progressLabel = `${response.batch.progress.complete}/${response.batch.progress.total} complete`;
        this.batchLabel = `Multi Search (${response.batch.totalInputs} IDs)`;
        this.batchSource = response.batch.source;
        const completionChargeTotal = Number(response.batch.chargeSummary?.completionChargeTotal || 0);
        if (this.lastCompletionChargeTotal !== completionChargeTotal) {
          this.lastCompletionChargeTotal = completionChargeTotal;
          this.apiService.requestUserInfoSummaryRefresh();
        }
        setTimeout(() => {
          if (this.paginator) this.dataSource.paginator = this.paginator;
          if (this.sort) this.dataSource.sort = this.sort;
        });
        this.isLoading = false;
        this.updatePolling(response.requests);
      },
      error: (error) => {
        this.isLoading = false;
        const errorMessage = error?.error?.error?.message || 'Failed to load multi search requests';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
      }
    });
  }

  getSearchCriteria(request: RequestListItem): string {
    if (request.rawInput) {
      if (request.lookupType === 'TRUST') {
        return `Trust: ${request.rawInput}`;
      }
      return `ID: ${request.rawInput}`;
    }
    if (request.lookupDisplay && request.lookupType) {
      if (request.lookupType === 'ID') {
        return `ID: ${this.maskIdNumber(request.lookupDisplay)}`;
      }
      if (request.lookupType === 'TRUST') {
        return `Trust: ${request.lookupDisplay}`;
      }
      return `Address: ${request.lookupDisplay}`;
    }
    return 'N/A';
  }

  getCustomerName(request: RequestListItem): string {
    const name = (request.customerName || '').trim();
    return name ? name : ' — ';
  }

  isIdLookup(request: RequestListItem): boolean {
    return request.lookupType === 'ID' && !!request.lookupDisplay && !request.rawInput;
  }

  getMaskedLookupId(request: RequestListItem): string {
    return this.maskIdNumber(request.lookupDisplay || '');
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'IN_PROGRESS': return 'hourglass_empty';
      case 'QUEUED': return 'queue';
      case 'FAILED': return 'error';
      case 'COMPLETE': return 'check_circle';
      case 'CANCELLED': return 'cancel';
      default: return 'help';
    }
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'IN_PROGRESS':
      case 'QUEUED':
        return 'accent';
      case 'FAILED':
        return 'warn';
      case 'COMPLETE':
        return 'primary';
      default:
        return '';
    }
  }

  formatStatusLabel(status: string): string {
    return status.replace(/_/g, ' ');
  }

  formatDate(timestamp: any): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).format(date);
  }

  getAvailabilityIcon(request: RequestListItem): string {
    if (request.status === 'FAILED') return 'error';
    const mode = request.viewAvailability?.mode;
    if (mode === 'NO_MATCH') return 'search_off';
    if (mode === 'FREE_UNTIL' || mode === 'FREE') return 'verified';
    if (mode === 'CREDIT_COST') return 'paid';
    if (mode === 'FAILED') return 'error';
    if (mode === 'INSUFFICIENT_CREDITS') return 'money_off';
    if (mode === 'UNAVAILABLE') return 'block';
    return 'hourglass_empty';
  }

  getAvailabilityIconColor(request: RequestListItem): string {
    const mode = request.viewAvailability?.mode;
    if (mode === 'FREE_UNTIL' || mode === 'FREE') return 'primary';
    if (mode === 'CREDIT_COST') return 'warn';
    return '';
  }

  getAvailabilityText(request: RequestListItem): string {
    if (request.status === 'FAILED') return 'Failed';
    const availability = request.viewAvailability;
    if (!availability) return '—';
    switch (availability.mode) {
      case 'FREE':
        return 'Free view';
      case 'FREE_UNTIL':
        return `Free until ${this.formatFreeUntilLabel(availability.freeUntil || null)}`;
      case 'CREDIT_COST':
        return this.formatCreditCostLabel(availability.creditCost);
      case 'NO_MATCH':
        return 'No result found';
      case 'UNAVAILABLE':
        return 'Not available';
      case 'INSUFFICIENT_CREDITS':
        return 'Not available - no credits';
      case 'FAILED':
        return 'Failed';
      case 'NOT_READY':
      default:
        return 'Available when complete';
    }
  }

  getAvailabilityTooltip(request: RequestListItem): string {
    if (request.status === 'FAILED') return 'This request failed. Please run a new search.';
    const availability = request.viewAvailability;
    if (!availability) return 'Availability unknown';
    switch (availability.mode) {
      case 'FREE':
        return 'Viewing this entry is free';
      case 'FREE_UNTIL': {
        const full = this.formatDate(availability.freeUntil);
        return full ? `Free view active until ${full}` : 'Free view active';
      }
      case 'CREDIT_COST':
        return `Viewing this entry will incur a cost (${this.formatCreditCostLabel(availability.creditCost)})`;
      case 'NO_MATCH':
        return 'No result found for this ID number.';
      case 'UNAVAILABLE':
        return 'This request is unavailable due to Do Not Contact status';
      case 'INSUFFICIENT_CREDITS':
        return 'This request completed when your credit balance was zero. Buy credits and run a new search.';
      case 'FAILED':
        return 'This request failed. Please run a new search.';
      case 'NOT_READY':
      default:
        return 'This request can be viewed once processing is complete';
    }
  }

  private formatFreeUntilLabel(value: string | null): string {
    if (!value) return 'soon';
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'soon';
    const time = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);

    const dayKey = this.dayKeyInJohannesburg(date);
    const now = new Date();
    const todayKey = this.dayKeyInJohannesburg(now);
    const tomorrowKey = this.dayKeyInJohannesburg(new Date(now.getTime() + 24 * 60 * 60 * 1000));
    if (dayKey === todayKey) return `${time} today`;
    if (dayKey === tomorrowKey) return `${time} tomorrow`;
    return `${time} on ${new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      day: '2-digit',
      month: '2-digit',
    }).format(date)}`;
  }

  private formatCreditCostLabel(value: number | null | undefined): string {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return 'credit cost unavailable';
    }
    const normalized = Math.max(1, Math.trunc(parsed));
    return `${normalized} credit${normalized === 1 ? '' : 's'}`;
  }

  private dayKeyInJohannesburg(date: Date): string {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  async viewData(request: RequestListItem): Promise<void> {
    const canAffordView = await this.canAffordCreditCostView(
      request.viewAvailability?.creditCost,
      request.viewAvailability?.mode
    );
    if (!canAffordView) {
      this.snackBar.open('You have insufficient credits for this request.', 'Close', { duration: 5000 });
      return;
    }
    this.analyticsService.trackEvent('multi_search_view_record_clicked', {
      request_batch_id: this.requestBatchId
    });
    const from = this.router.url.startsWith('/user/') ? 'user-multi-search' : 'multi-search';
    if (request.lookupType === 'TRUST') {
      const base = this.router.url.startsWith('/user/') ? '/user/trust-data' : '/admin/trust-data';
      void this.router.navigate([base, request.requestId], {
        queryParams: { from, batchId: this.requestBatchId }
      });
      return;
    }
    void this.router.navigate(['/admin/data', request.requestId], {
      queryParams: { from, batchId: this.requestBatchId }
    });
  }

  canViewRequest(request: RequestListItem): boolean {
    if (request.status !== 'COMPLETE') return false;
    if (request.dncBlocked === true) return false;
    if (request.viewAvailability?.mode === 'UNAVAILABLE') return false;
    if (request.viewAvailability?.mode === 'INSUFFICIENT_CREDITS') return false;
    if (request.viewAvailability?.mode === 'NO_MATCH') return false;
    if (request.viewAvailability?.mode === 'NOT_READY') return false;
    return true;
  }

  canMarkDnc(request: RequestListItem): boolean {
    if (request.lookupType === 'TRUST') return false;
    if (request.status !== 'COMPLETE') return false;
    if (!request.requestId) return false;
    if (request.dncBlocked === true) return false;
    if (request.viewAvailability?.mode === 'NO_MATCH') return false;
    return true;
  }

  isMarkDncLoading(request: RequestListItem): boolean {
    return this.dncLoadingByRequestId.has(request.requestId);
  }

  getMarkDncTooltip(request: RequestListItem): string {
    if (request.dncBlocked === true) return 'This contact is already marked as Do Not Contact';
    if (request.viewAvailability?.mode === 'NO_MATCH') return 'No result found for this ID number.';
    if (request.status !== 'COMPLETE') return 'This request can be marked once processing is complete';
    return 'Mark as Do Not Contact';
  }

  markAsDnc(request: RequestListItem): void {
    if (!this.canMarkDnc(request)) return;
    if (this.dncLoadingByRequestId.has(request.requestId)) return;

    const proceed = confirm(
      'Mark this contact as Do Not Contact (DNC)?\n\n' +
      'This will block future contact actions for this contact in your company and ' +
      'access will be restricted after confirmation.'
    );
    if (!proceed) return;

    this.dncLoadingByRequestId.add(request.requestId);
    this.apiService.markRequestDnc(request.requestId).subscribe({
      next: () => {
        this.snackBar.open(
          'Contact marked as Do Not Contact for your company. View access is now blocked',
          'Close',
          { duration: 5000 }
        );
        request.dncBlocked = true;
        if (request.viewAvailability) {
          request.viewAvailability = {
            ...request.viewAvailability,
            mode: 'UNAVAILABLE',
          };
        }
        this.dncLoadingByRequestId.delete(request.requestId);
      },
      error: (error) => {
        const errorMessage = error?.error?.error?.message || 'Failed to mark contact as Do Not Contact';
        this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
        this.dncLoadingByRequestId.delete(request.requestId);
      }
    });
  }

  getViewDisabledReason(request: RequestListItem): string {
    if (request.dncBlocked === true || request.viewAvailability?.mode === 'UNAVAILABLE') {
      return 'This request is unavailable due to Do Not Contact status';
    }
    if (request.viewAvailability?.mode === 'INSUFFICIENT_CREDITS') {
      return 'This request is unavailable because you had no credits when it completed.';
    }
    if (request.viewAvailability?.mode === 'NO_MATCH') {
      return 'No result found for this ID number.';
    }
    if (request.viewAvailability?.mode === 'FAILED' || request.status === 'FAILED') {
      return 'This request failed. Please run a new search.';
    }
    if (request.viewAvailability?.mode === 'NOT_READY' || request.status !== 'COMPLETE') {
      return 'This request can be viewed once processing is complete';
    }
    return '';
  }

  goBack(): void {
    if (this.openedFromAddressBook) {
      void this.router.navigate(['/admin/address-book']);
      return;
    }
    if (this.openedFromUserAddressBook) {
      void this.router.navigate(['/user/address-book']);
      return;
    }
    if (this.router.url.startsWith('/user/')) {
      if (this.batchSource === 'CSV_UPLOAD') {
        void this.router.navigate(['/user/data-request']);
        return;
      }
      void this.router.navigate(['/user/address-book']);
      return;
    }
    if (this.batchSource === 'CSV_UPLOAD') {
      void this.router.navigate(['/admin/new-request']);
      return;
    }
    void this.router.navigate(['/admin/address-book']);
  }

  async downloadBatchPdf(): Promise<void> {
    await this.downloadBatch('pdf');
  }

  async downloadBatchExcel(): Promise<void> {
    await this.downloadBatch('excel');
  }

  private async downloadBatch(format: 'pdf' | 'excel'): Promise<void> {
    if (this.isDownloading) return;
    if (this.hasActiveSearchItems()) {
      this.snackBar.open(
        'Search is still in progress. You can download once all records finish processing.',
        'Close',
        { duration: 5000 }
      );
      return;
    }

    const orderedRequests = this.getRequestsInInputOrder();
    if (!orderedRequests.length) {
      this.snackBar.open('No records are available to export', 'Close', { duration: 5000 });
      return;
    }

    this.isDownloading = true;
    this.analyticsService.trackEvent(
      format === 'pdf' ? 'multi_search_pdf_download_started' : 'multi_search_excel_download_started',
      {
      request_batch_id: this.requestBatchId,
      exportable_records: orderedRequests.length
      }
    );
    try {
      const dialogRef = this.dialog.open(ConsentModalComponent, {
        width: '620px',
        disableClose: true,
        autoFocus: false,
        data: {
          title: 'Download Data Consent',
          body: [
            'By accepting, you confirm that this personal information will be processed only for the specific lawful purpose agreed to when your organisation subscribed to this service.',
            'In terms of POPIA principles, you acknowledge that once downloaded you become a responsible custodian of this data and must apply appropriate security safeguards, confidentiality, and access controls.',
          ],
          acceptLabel: 'Accept',
          rejectLabel: 'Reject',
        },
      });

      const consentResult = await firstValueFrom(dialogRef.afterClosed()) as ConsentData | undefined;
      if (!consentResult) {
        this.snackBar.open('Download consent was not completed', 'Close', { duration: 4000 });
        return;
      }

      await firstValueFrom(
        this.apiService.submitBatchDownloadConsent(this.requestBatchId, { action: consentResult.action })
      );
      if (consentResult.action !== 'accept') {
        this.snackBar.open('Download cancelled because consent was rejected', 'Close', { duration: 4000 });
        return;
      }

      const rows: RecordPdfRow[] = [];
      let failedCount = 0;
      const dataByRequestId = new Map<string, RequestDataResponse>();
      for (const request of orderedRequests) {
        if (!this.canViewRequest(request)) {
          rows.push(this.toUnavailableRow(request));
          continue;
        }
        let data = dataByRequestId.get(request.requestId);
        if (!data) {
          try {
            data = await firstValueFrom(this.apiService.getRequestData(request.requestId));
            dataByRequestId.set(request.requestId, data);
          } catch (error) {
            failedCount += 1;
            rows.push(this.toUnavailableRow(request));
            continue;
          }
        }
        rows.push(this.toPdfRow(request, data));
      }

      if (!rows.length) {
        this.snackBar.open('No records could be exported. Please verify POPIA acknowledgement and availability', 'Close', { duration: 6000 });
        return;
      }

      if (format === 'pdf') {
        await exportRecordsPdf(
          `Multi Search ${this.requestBatchId}`,
          rows,
          `multi-search-${this.requestBatchId}-${new Date().toISOString().slice(0, 10)}.pdf`
        );
      } else {
        exportRecordsExcel(
          rows,
          `multi-search-${this.requestBatchId}-${new Date().toISOString().slice(0, 10)}.xlsx`
        );
      }
      this.analyticsService.trackEvent(
        format === 'pdf' ? 'multi_search_pdf_download_succeeded' : 'multi_search_excel_download_succeeded',
        {
        request_batch_id: this.requestBatchId,
        exported_records: rows.length,
        failed_records: failedCount
        }
      );

      if (failedCount > 0) {
        const formatLabel = format === 'pdf' ? 'PDF' : 'Excel file';
        this.snackBar.open(
          `${failedCount} record${failedCount === 1 ? '' : 's'} could not be fetched and ${failedCount === 1 ? 'was' : 'were'} exported with "-" placeholders in the ${formatLabel}`,
          'Close',
          { duration: 5000 }
        );
      }
    } catch (error: any) {
      this.analyticsService.trackEvent(
        format === 'pdf' ? 'multi_search_pdf_download_failed' : 'multi_search_excel_download_failed',
        {
          request_batch_id: this.requestBatchId
        }
      );
      const formatLabel = format === 'pdf' ? 'PDF' : 'Excel';
      const errorMessage = error?.error?.error?.message || `Failed to download multi search ${formatLabel}`;
      this.snackBar.open(errorMessage, 'Close', { duration: 5000 });
    } finally {
      this.isDownloading = false;
    }
  }

  private toPdfRow(request: RequestListItem, response: RequestDataResponse): RecordPdfRow {
    const profile = response.profile;
    const fullName = (request.customerName || '').trim() || [profile?.firstName, profile?.surname].filter(Boolean).join(' ').trim();
    const phoneValues = (profile?.topTelephoneNumbers || [])
      .slice(0, 8)
      .map((phone) => this.normalizePhoneForExport(phone.number))
      .filter((value) => value && value !== 'Unknown number');

    return {
      name: fullName || '-',
      dob: profile?.birthDate ? this.formatDateOnly(profile.birthDate) : '-',
      idNumber: request.rawInput || profile?.idNumber || '-',
      telephone1: phoneValues[0] || '-',
      telephone2: phoneValues[1] || '-',
      telephone3: phoneValues[2] || '-',
      telephone4: phoneValues[3] || '-',
      telephone5: phoneValues[4] || '-',
      telephone6: phoneValues[5] || '-',
      telephone7: phoneValues[6] || '-',
      telephone8: phoneValues[7] || '-',
    };
  }

  private toUnavailableRow(request: RequestListItem): RecordPdfRow {
    return {
      name: '-',
      dob: '-',
      idNumber: request.rawInput || request.lookupDisplay || '-',
      telephone1: '-',
      telephone2: '-',
      telephone3: '-',
      telephone4: '-',
      telephone5: '-',
      telephone6: '-',
      telephone7: '-',
      telephone8: '-',
    };
  }

  private formatDateOnly(value: unknown): string {
    if (!value) return 'N/A';
    const date = new Date(String(value));
    if (isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(date);
  }

  private formatPhoneDisplay(number?: string | null): string {
    if (!number) return 'Unknown number';
    const digits = number.replace(/\D/g, '');
    if (!digits) return number;
    if (digits.length === 10) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    if (digits.length === 9) return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    const grouped = digits.match(/.{1,3}/g);
    return grouped ? grouped.join(' ') : digits;
  }

  private normalizePhoneForExport(number?: string | null): string {
    if (!number) return 'Unknown number';
    const compact = number.replace(/\s+/g, '').trim();
    return compact || 'Unknown number';
  }

  private maskIdNumber(value: string): string {
    const compact = value.replace(/\s+/g, '');
    if (!compact) return 'N/A';
    if (compact.length <= 6) return compact;
    return `${'*'.repeat(compact.length - 6)}${compact.slice(-6)}`;
  }

  private getRequestsInInputOrder(): RequestListItem[] {
    return [...this.requestsInInputOrder];
  }

  private hasActiveSearchItems(): boolean {
    return this.dataSource.data.some((request) =>
      request.status === 'IN_PROGRESS' || request.status === 'QUEUED'
    );
  }

  private updatePolling(requests: RequestListItem[]): void {
    const hasActive = requests.some((r) => r.status === 'IN_PROGRESS' || r.status === 'QUEUED');
    if (!hasActive) {
      this.stopPolling();
      return;
    }
    if (this.pollSub) return;
    this.pollSub = interval(4000).subscribe(() => this.loadRequests(false));
  }

  private stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = null;
  }

  private async canAffordCreditCostView(
    creditCost: number | null | undefined,
    mode: 'FREE' | 'FREE_UNTIL' | 'CREDIT_COST' | 'NO_MATCH' | 'FAILED' | 'NOT_READY' | 'UNAVAILABLE' | 'INSUFFICIENT_CREDITS' | undefined
  ): Promise<boolean> {
    if (mode !== 'CREDIT_COST') return true;
    const requiredCredits = Number(creditCost);
    if (!Number.isFinite(requiredCredits) || requiredCredits <= 0) return true;
    try {
      const summary = await firstValueFrom(this.apiService.getUserInfoSummary());
      const creditsRemaining = Number(summary?.credits?.creditsRemaining || 0);
      return creditsRemaining >= Math.max(1, Math.trunc(requiredCredits));
    } catch {
      // Fail open if summary cannot be loaded.
      return true;
    }
  }
}
